import React from 'react';
import PropTypes from 'prop-types';
import { Box, Skeleton, Stack, Tooltip, Typography } from '@mui/material';
import Iconify from '../iconify';
import { bandStyle, bucketsOf, summaryLabel, summaryState } from '../../sections/@dashboard/rules/ruleBands';

/**
 * The per-recording severity bar on the Recordings list.
 *
 * Renders entirely from the embedded ruleEvaluationSummary, so a page of
 * recordings costs no extra requests.
 *
 * Severity is carried by the label as well as the colour. Colour alone would make
 * the whole column unreadable to a red/green colour-blind user, and this column
 * is the only place the list says anything about air quality at all.
 */

ViolationBar.propTypes = {
  summary: PropTypes.object,
  stale: PropTypes.bool,
  compact: PropTypes.bool,
};

const CELL_GAP = 1;

function ViolationBar({ summary, stale = false, compact = false }) {
  const state = summaryState(summary);
  const buckets = bucketsOf(summary);
  const label = summaryLabel(summary);

  if (state === 'pending') {
    return (
      <Stack direction="row" alignItems="center" spacing={1}>
        <Skeleton variant="rounded" width={compact ? 80 : 120} height={10} />
        <Typography variant="caption" color="text.secondary">
          {label}
        </Typography>
      </Stack>
    );
  }

  if (state === 'failed' || state === 'unscored') {
    // Never a bar here: an empty bar reads as "clean", and neither of these is.
    return (
      <Tooltip title={summary?.failureReason || 'This recording has not been scored against your rules.'}>
        <Stack direction="row" alignItems="center" spacing={0.5} sx={{ color: 'text.disabled' }}>
          <Iconify icon={state === 'failed' ? 'eva:alert-triangle-outline' : 'eva:clock-outline'} width={16} />
          <Typography variant="caption">{label}</Typography>
        </Stack>
      </Tooltip>
    );
  }

  const tooltip = [
    label,
    state === 'broken' ? `Worst: ${bandStyle(summary.maxBand).label}` : null,
    state === 'broken' ? `${summary.violationCount} violation${summary.violationCount === 1 ? '' : 's'}` : null,
    stale ? 'Your rules changed since this was scored — re-evaluate to refresh.' : null,
  ]
    .filter(Boolean)
    .join(' · ');

  return (
    <Tooltip title={tooltip}>
      {/* The label lives on the bar alone. Repeating it on the wrapper makes a
          screen reader announce the whole summary twice. */}
      <Stack direction="row" alignItems="center" spacing={1} sx={{ opacity: stale ? 0.5 : 1 }}>
        {/* Decorative: MUI's Tooltip injects the accessible name onto the wrapper
            it clones, and the text label beside this bar carries the same
            information. Labelling it here too would announce everything twice. */}
        <Box
          aria-hidden="true"
          data-testid="violation-bar-cells"
          sx={{
            display: 'flex',
            gap: `${CELL_GAP}px`,
            width: compact ? 80 : 120,
            height: 10,
            borderRadius: 0.5,
            overflow: 'hidden',
          }}
        >
          {buckets.length === 0 ? (
            <Box sx={{ flex: 1, bgcolor: bandStyle(0).color }} />
          ) : (
            buckets.map((band, i) => (
              <Box
                // eslint-disable-next-line react/no-array-index-key
                key={i}
                sx={{ flex: 1, bgcolor: bandStyle(band).color }}
              />
            ))
          )}
        </Box>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          {state === 'broken' ? (
            <Iconify icon="eva:alert-circle-fill" width={14} sx={{ color: bandStyle(summary.maxBand).color }} />
          ) : (
            <Iconify icon="eva:checkmark-circle-2-outline" width={14} sx={{ color: 'success.main' }} />
          )}
          <Typography variant="caption" color={state === 'broken' ? 'text.primary' : 'text.secondary'} noWrap>
            {label}
          </Typography>
          {stale && <Iconify icon="eva:refresh-outline" width={12} sx={{ color: 'text.disabled' }} />}
        </Stack>
      </Stack>
    </Tooltip>
  );
}

export default ViolationBar;
